import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {MatLineModule} from '@angular/material/core';
import {MatDividerModule} from '@angular/material/divider';
import {clearSelectedMap, GraphMap} from '../api/interfaces/graph-node.interface';
import {CommonModule} from '@angular/common';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {CreateNewMapDialogComponent} from './create-new-map-dialog.component';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {Subscription, switchMap, timer} from 'rxjs';
import {MatTooltip, MatTooltipModule} from '@angular/material/tooltip';
import {ImportDialogData, ImportMapDialogComponent} from './import-map.dialog.component';
import {ImportSmapDialogComponent} from './import-smap-dialog.component';
import {GenerateSmapPromptDialogComponent} from './generate-smap-prompt.dialog.component';
import {OpenaiService} from '../api/services/openai.service';
import {JobService} from '../api/services/job.service';
import {Router} from '@angular/router';
import {PackageService} from '../api/services/package.service';
import {MODULE_NAMES, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {ToursService} from '../api/services/tour.service';
import {MapDialogsService} from './map-dialogs.service';
import {MapRoleService} from '../acl/map-role.service';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {HasPackageDirective} from '../packages/has-package.directive';
import {TopToolbarStateService} from '../nav/top-toolbar-state.service';
import {StoreService} from '../api/services/store.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-map-list',
  styleUrls: ['./map-list.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-object-smap me-2"></span>{{ 'MAP_LIST.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>

    @if (maps && maps.length > 0) {
      <div class="d-flex align-items-center justify-content-between py-2 smaps-header-row">
        <input type="text"
               [placeholder]="'TOOLBAR.SEARCH' | translate"
               class="smaps-search"
               id="search-input"
               #searchInput
               autocomplete="off"
               autocapitalize="off"
               autocorrect="off"
               spellcheck="false"
               [formControl]="searchControl">
        <span class="smaps-count">{{ getMapCountWithRespectToFilterAndSearch() }}</span>
        <app-select-input [formControl]="filtersControl"
                          [blankValue]="'MAP_LIST.FILTER_BY' | translate"
                          [noMargin]="true"
                          [dropdownColSize]="20"
                          [isIcon]="false"
                          class="d-flex"
                          [noIcons]="true"
                          [options]="filterOptions">
        </app-select-input>
      </div>
      <mat-divider></mat-divider>
    }
    <mat-nav-list class="list-wrapper">
      <a mat-list-item *ngFor="let map of filteredMaps"
         (click)="selectMap(map); $event.preventDefault(); $event.stopPropagation()"
         class="d-flex justify-content-evenly p-0">
        <span class="d-flex w-100 align-items-center m-0 smap-row">
          <span class="smap-access">
            <button mat-icon-button
                    [matTooltip]="getToolTip(map)"
                    matTooltipPosition="above">
                <span [ngClass]="[getMapAccessIcon(map)]"></span>
            </button>
          </span>
          <span class="smap-name flex-grow-1"
                [matTooltip]="map.description || ''"
                matTooltipPosition="below">
              <span matLine>{{ map.name }}</span>
          </span>
          <span class="smap-credit-cost align-items-center"
                [matTooltip]="'MAP_LIST.AI_CREDITS_TOOLTIP' | translate">
            {{ (map.aiCreditsUsed ?? 0) | number:'1.0-0' }}
          </span>
          <span class="smap-actions d-flex justify-content-end">
            <button mat-icon-button
                    [matTooltip]="'MAP_LIST.EDIT_INFO' | translate"
                    [disabled]="map.isReadonly === true || isDefaulter"
                    (click)="edit(map);$event.preventDefault();  $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">edit</mat-icon>
            </button>
            <button mat-icon-button
                    [matTooltip]="'MAP_LIST.DUPLICATE' | translate"
                    [disabled]="map.isReadonly === true || isDefaulter"
                    (click)="duplicate(map); $event.preventDefault();  $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">file_copy</mat-icon>
            </button>
            <button mat-icon-button
                    [matTooltip]="'MAP_LIST.EXPORT' | translate"
                    *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer, PACKAGE_NAMES.defaulter]"
                    [disabled]="map.isReadonly === true || !hasModuleExport"
                    (click)="download(map);$event.preventDefault(); $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">download</mat-icon>
            </button>
            <span *ngIf="!map.isReadonly">
                <button mat-icon-button
                        [matTooltip]="'MAP_LIST.DELETE' | translate"
                        (click)="deleteConfirmation(map);$event.preventDefault();  $event.stopPropagation()">
                    <mat-icon class="material-symbols-rounded">delete</mat-icon>
                </button>
            </span>
            <span *ngIf="map.isReadonly">
              <button mat-icon-button
                      [matTooltip]="(isInLibrary(map) && !isSharedWithMe(map) ? 'MAP_LIST.REMOVE_FROM_COLLECTION' : 'MAP_LIST.REMOVE_ACCESS') | translate"
                      (click)="unshareConfirmation(map);$event.preventDefault();  $event.stopPropagation()">
                  <mat-icon *ngIf="isInLibrary(map) && !isSharedWithMe(map)"
                            class="material-symbols-rounded">remove</mat-icon>
                  <mat-icon *ngIf="!isInLibrary(map) || isSharedWithMe(map)" class="material-symbols-rounded">person_remove</mat-icon>
              </button>
            </span>
          </span>
        </span>
      </a>
    </mat-nav-list>
    <mat-divider></mat-divider>
    @if (!isDefaulter) {
      <div class="py-2 d-flex justify-content-between align-items-center" style="padding-left: 10px; padding-right: 10px;">
                <button class="dialog-action-btn dialog-action-btn-primary"
                        [matTooltip]="'MAP_LIST.CREATE_NEW_TOOLTIP' | translate"
                        (click)="createNew(); $event.preventDefault();">
                  {{ 'MAP_LIST.CREATE_SMAP' | translate }}
                </button>
                <button class="dialog-action-btn dialog-action-btn-primary"
                        [matTooltip]="'MAP_LIST.IMPORT_TOOLTIP' | translate"
                        (click)="importSmapArchive(); $event.preventDefault();">
                  {{ 'MAP_LIST.IMPORT_SMAP' | translate }}
                </button>
      </div>
    }
  `,

  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    SelectInputComponent,
    HasPackageDirective,
    TranslatePipe
  ]
})

export class MapListBottomsheetComponent implements OnInit, AfterViewInit, OnDestroy {
  public maps: GraphMap[] = [];
  public filteredMaps: GraphMap[] = [];

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChildren(MatTooltip) tooltips!: QueryList<MatTooltip>;

  private subSink: Subscription = new Subscription();

  filterOptions: { key: string; value: string; isSeparatorAfter?: boolean }[] = [];

  filtersControl = new FormControl('all');
  searchControl = new FormControl('');

  constructor(private matDialog: MatDialog,
              private mapService: MapService,
              private userService: UserService,
              private mapDialogsService: MapDialogsService,
              private packageService: PackageService,
              private mapRoleService: MapRoleService,
              private tourService: ToursService,
              private openaiService: OpenaiService,
              private jobService: JobService,
              private router: Router,
              private topToolbarStateService: TopToolbarStateService,
              private storeService: StoreService,
              private translate: TranslateService,
              private bottomSheetRef: MatBottomSheetRef<MapListBottomsheetComponent>) {
  }

  ngOnInit() {
    this.filterOptions = [
      { key: 'all', value: this.translate.instant('MAP_LIST.FILTER_ALL'), isSeparatorAfter: true },

      { key: 'ownedByMe', value: this.translate.instant('MAP_LIST.FILTER_OWNED_BY_ME') },
      {key: 'managedByMe', value: this.translate.instant('MAP_LIST.FILTER_MANAGED_BY_ME'), isSeparatorAfter: true},
      { key: 'sharedByMe', value: this.translate.instant('MAP_LIST.FILTER_SHARED_BY_ME') },
      { key: 'sharedWithMe', value: this.translate.instant('MAP_LIST.FILTER_SHARED_WITH_ME'), isSeparatorAfter: true },

      { key: 'template', value: this.translate.instant('MAP_LIST.FILTER_TEMPLATES') },
      { key: 'smaplet', value: this.translate.instant('MAP_LIST.FILTER_SMAPLETS') }
    ];

    this.subSink.add(
      timer(0, 10000)
        .pipe(switchMap(() => this.mapService.findAllForUser(this.userService.getCurrentUserFromStorage().id)))
        .subscribe((maps) => {
          this.maps = maps;
          this.applyFilters();
        })
    );
    this.subSink.add(this.searchControl.valueChanges.subscribe(() => this.applyFilters()));
    this.subSink.add(this.filtersControl.valueChanges.subscribe(() => this.applyFilters()));
  }

  ngAfterViewInit() {
    // Auto-focus the search input when the component is shown
    setTimeout(() => {
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.focus();
      }
    }, 1100);
  }

  get hasModuleExport() {
    return this.packageService.hasModule(MODULE_NAMES.MAP_EXPORT)
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  applyFilters() {
    const searchText = this.searchControl.value?.toLowerCase() || '';
    const filterKey = this.filtersControl.value;

    this.filteredMaps = this.maps.filter((map) => {
      const matchesSearch = map.name.toLowerCase().includes(searchText) ||
                           map.description?.toLowerCase().includes(searchText);

      const currentUserId = this.userService.getCurrentUserFromStorage().id;
      const isOwner = map.owner?.id === currentUserId;
      const isSharedWithMe = !isOwner && map.userRoles?.some(r => r.user.id === currentUserId);
      // @ts-ignore
      const isSharedByMe = isOwner && map.sharedWith?.length > 0;
      const isManager = map.userRoles?.some(r => r.user.id === currentUserId && r.role === 'manager');
      const isManagedByMe = isOwner || isManager;

      const canCreateFromTemplate = isOwner || map.allowViewersToCreateFromTemplate;
      const matchesFilter =
        filterKey === 'all' ||
        (filterKey === 'ownedByMe' && isOwner) ||
        (filterKey === 'managedByMe' && isManagedByMe) ||
        (filterKey === 'sharedByMe' && isSharedByMe) ||
        (filterKey === 'sharedWithMe' && isSharedWithMe) ||
        (filterKey === 'template' && map.isTemplate && canCreateFromTemplate) ||
        (filterKey === 'smaplet' && map.isPublic) ||
        (filterKey === 'regular' && !map.isTemplate && !map.isPublic);

      return matchesSearch && matchesFilter;
    });
  }

  getMapAccessIcon(map: GraphMap) {
    const currentUserId = this.userService.getCurrentUserFromStorage()?.id;
    const isOwner = map.owner?.id === currentUserId;

    // Check if map was added from store library (non-owners only, and not shared with user)
    // Sharing takes precedence over library - shared users should see role-based icons
    if (!isOwner && this.isInLibrary(map) && !this.isSharedWithMe(map)) {
      // Use template icon if it's an authorized template, otherwise viewer icon
      if (map.isTemplate && map.allowViewersToCreateFromTemplate) {
        return 'icon-smap-object-template';
      }
      return 'icon-smap-role-viewer';
    }

    // Starter maps or readonly maps should always show role-based icons
    if (map.isStarter || map.isReadonly) {
      const currentUsersRoleForMap = map.userRoles?.find((role) => role.user.id === currentUserId);
      const userRole = currentUsersRoleForMap?.role || 'viewer';

      switch (userRole) {
        case 'manager':
          return 'icon-smap-role-manager';
        case 'editor':
          return 'icon-smap-role-editor';
        case 'commenter':
          return 'icon-smap-role-commenter';
        default:
          return 'icon-smap-role-viewer';
      }
    } else if (isOwner) {
      // User is the owner of a regular map - show owner icons
      return (map.sharedWith?.length || 0) > 0 ? 'icon-smap-role-owner-shared' : 'icon-smap-role-owner';
    } else {
      // User is not the owner - show their role icon
      const currentUsersRoleForMap = map.userRoles?.find((role) => role.user.id === currentUserId);
      const userRole = currentUsersRoleForMap?.role || 'viewer';

      switch (userRole) {
        case 'manager':
          return 'icon-smap-role-manager';
        case 'editor':
          return 'icon-smap-role-editor';
        case 'commenter':
          return 'icon-smap-role-commenter';
        default:
          return 'icon-smap-role-viewer';
      }
    }
  }


  selectMap(map: GraphMap) {
    this.tourService.activateTourSubject.next(null);
    this.hideAllTooltips();
    this.bottomSheetRef.dismiss(map);
  }

  private hideAllTooltips() {
    // Disable (not just hide) so tooltip fading can't leave one shown, and so no
    // late/re-hover show() fires while the sheet slides closed (MAP-1536).
    this.tooltips?.forEach(t => t.disabled = true);
  }

  getToolTip(map: GraphMap) {
    const currentUserId = this.userService.getCurrentUserFromStorage()?.id;
    const isOwner = map.owner?.id === currentUserId;
    const sharedWithCount = map.sharedWith?.length || 0;

    // Check if map was added from store library (non-owners only, and not shared with user)
    // Sharing takes precedence over library - shared users should see role-based tooltips
    if (!isOwner && this.isInLibrary(map) && !this.isSharedWithMe(map)) {
      return this.translate.instant('MAP_LIST.TOOLTIP_ADDED_FROM_STORE');
    }

    // Readonly maps should always show role-based tooltips
    if (map.isReadonly) {
      const currentUsersRoleForMap = map.userRoles?.find((role) => role.user.id === currentUserId);
      const userRole = currentUsersRoleForMap?.role || 'viewer';
      const ownerFullName = `${map.owner?.name || ''} ${map.owner?.lastName || ''}`.trim();

      if (isOwner) {
        switch (userRole) {
          case 'manager':
            return this.translate.instant('MAP_LIST.ROLE_MANAGER');
          case 'editor':
            return this.translate.instant('MAP_LIST.ROLE_EDITOR');
          case 'commenter':
            return this.translate.instant('MAP_LIST.ROLE_COMMENTER');
          default:
            return this.translate.instant('MAP_LIST.ROLE_VIEWER');
        }
      } else {
        switch (userRole) {
          case 'manager':
            return this.translate.instant('MAP_LIST.ROLE_MANAGER_OWNED_BY', { owner: ownerFullName });
          case 'editor':
            return this.translate.instant('MAP_LIST.ROLE_EDITOR_OWNED_BY', { owner: ownerFullName });
          case 'commenter':
            return this.translate.instant('MAP_LIST.ROLE_COMMENTER_OWNED_BY', { owner: ownerFullName });
          default:
            return this.translate.instant('MAP_LIST.ROLE_VIEWER_OWNED_BY', { owner: ownerFullName });
        }
      }
    } else if (isOwner) {
      // User is the owner of a regular map
      if (sharedWithCount > 0) {
        return this.translate.instant(
          sharedWithCount === 1 ? 'MAP_LIST.OWNER_SHARED_SINGULAR' : 'MAP_LIST.OWNER_SHARED_PLURAL',
          { count: sharedWithCount }
        );
      } else {
        return this.translate.instant('MAP_LIST.OWNER');
      }
    } else {
      // User is not the owner - determine their role
      const currentUsersRoleForMap = map.userRoles?.find((role) => role.user.id === currentUserId);
      const userRole = currentUsersRoleForMap?.role || 'viewer';
      const ownerFullName = `${map.owner?.name || ''} ${map.owner?.lastName || ''}`.trim();

      switch (userRole) {
        case 'manager':
          return this.translate.instant('MAP_LIST.ROLE_MANAGER_OWNED_BY', { owner: ownerFullName });
        case 'editor':
          return this.translate.instant('MAP_LIST.ROLE_EDITOR_OWNED_BY', { owner: ownerFullName });
        case 'commenter':
          return this.translate.instant('MAP_LIST.ROLE_COMMENTER_OWNED_BY', { owner: ownerFullName });
        default:
          return this.translate.instant('MAP_LIST.ROLE_VIEWER_OWNED_BY', { owner: ownerFullName });
      }
    }
  }

  createNew() {
    const ref = this.matDialog.open(CreateNewMapDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      autoFocus: 'input'
    });
    ref.afterClosed().subscribe((map: GraphMap) => {
      if (map) {
        this.tourService.activateTourSubject.next(null);
        if ((map as any).isFromTemplate) {
          this.bottomSheetRef.dismiss({...map, isNew: true});
        } else {
          this.mapService.createDefaultMap(map).subscribe((createdMap: GraphMap) => {
            this.bottomSheetRef.dismiss({...createdMap, isNew: true});
          });
        }
      }
    });
  }

  edit(map: GraphMap) {
    this.mapDialogsService.edit(map).subscribe((updatedMap: GraphMap) => {
      if (updatedMap) {
        this.maps = this.maps.map((m) => {
          if (m.id === updatedMap.id) {
            return updatedMap;
          }
          return m;
        });
        this.applyFilters();

        if(this.mapService.getCurrentSelectedMapFromStore()?.id === updatedMap.id) {
          this.mapService.setCurrentSelectedMapToStore(updatedMap);
          this.mapService.setMapNameSignal(updatedMap.name);
          this.topToolbarStateService.setMapName(updatedMap.name);
          this.mapService.mapNameChangedSubject.next(updatedMap.name);
        }
      }
    });
  }

  duplicate(map: GraphMap) {
    this.mapDialogsService.duplicate(map).subscribe((m: GraphMap) => {
      if (m) {
        this.tourService.activateTourSubject.next(null);
        //@ts-ignore
        m.isNew = true;
        this.bottomSheetRef.dismiss(m);
      }
    });
  }

  delete(map: GraphMap) {
    this.mapService.delete(map.id).subscribe((res) => {
      const storedMap = this.mapService.getCurrentSelectedMapFromStore();

      if (storedMap && storedMap.id === map.id) {
        this.mapService.removeCurrentSelectedMapFromStore();
      }
      const mapsWithoutDeleted = this.maps.filter((m) => m.id !== map.id);
      this.maps = mapsWithoutDeleted;
      if (mapsWithoutDeleted.length > 0) {
        this.mapService.mapChangedSubject.next(mapsWithoutDeleted[0]);
        this.mapService.setCurrentSelectedMapToStore(mapsWithoutDeleted[0])
      } else {
        this.mapService.mapChangedSubject.next(undefined);
        this.mapService.setCurrentSelectedMapToStore(undefined);
        this.topToolbarStateService.setMapName(undefined);
        this.mapService.setMapNameSignal('');
        this.bottomSheetRef.dismiss();
        this.router.navigate(['/dashboard']);
      }
    });
  }

  unshare(map: GraphMap) {
    // Sharing takes precedence over library - if shared, always unshare
    // Only treat as store map if it's in library AND not shared with user
    const isShared = this.isSharedWithMe(map);
    const isStoreMap = !isShared && this.isInLibrary(map);

    // Remove from store library or unshare depending on map type
    const removeOperation = isStoreMap
      ? this.storeService.removeFromLibrary(map.id)
      : this.mapService.unshareMap(map.id);

    removeOperation.subscribe((res) => {
      const currentMap = this.mapService.getCurrentSelectedMapFromStore();
      if (currentMap && currentMap.id === map.id) {
        this.mapService.removeCurrentSelectedMapFromStore();
      }
      const mapsWithoutDeleted = this.maps.filter((m) => m.id !== map.id);
      this.maps = mapsWithoutDeleted;
      if (mapsWithoutDeleted.length > 0) {
        //TODO: Subject is deprecated
        this.mapService.mapChangedSubject.next(mapsWithoutDeleted[0]);
        this.mapService.setCurrentSelectedMapToStore(mapsWithoutDeleted[0])
      } else {
        clearSelectedMap();
        this.mapService.mapChangedSubject.next(undefined);
        this.mapService.setCurrentSelectedMapToStore(undefined)
      }
    });
  }

  download(map: GraphMap) {
    this.mapDialogsService.download(map);
  }


  deleteConfirmation(map: GraphMap) {
    this.mapDialogsService.deleteConfirmation(map).subscribe((res) => {
      if (res) {
        this.delete(map);
      }
    });
  }

  unshareConfirmation(map: GraphMap) {
    // Sharing takes precedence over library - if shared, always unshare
    // Only treat as store map if it's in library AND not shared with user
    const isShared = this.isSharedWithMe(map);
    const isStoreMap = !isShared && this.isInLibrary(map);
    this.mapDialogsService.deleteConfirmation(map, true, isStoreMap).subscribe((res) => {
      if (res) {
        this.unshare(map);
      }
    });
  }

  importMap() {
    const ref = this.matDialog.open(ImportMapDialogComponent, {
      width: '35%',
      hasBackdrop: false,
      data: {}
    });
  }

  importSmapArchive() {
    this.matDialog.open(ImportSmapDialogComponent, {
      width: '35%',
      hasBackdrop: false
    });
  }

  importPdfDocument() {
    const ref = this.matDialog.open(ImportMapDialogComponent, {
      width: '35%',
      hasBackdrop: false,
      data: { importType: 'pdf' } as ImportDialogData
    });
  }

  generateSmapFromPrompt() {
    this.matDialog.open(GenerateSmapPromptDialogComponent, {
      width: '35%',
      hasBackdrop: false
    });
  }

  // If no filter is applied return the count of all maps
  // If filter is applied return the count of maps that match the filter
  // if not filtered return 'x smaps'
  // if filter return 'y of x smaps'
  getMapCountWithRespectToFilterAndSearch() {
    const searchText = this.searchControl.value?.toLowerCase() || '';
    const filterKey = this.filtersControl.value;

    const isSearchActive = !!searchText;

    const filteredByFilter = this.maps.filter((map) => {
      const currentUserId = this.userService.getCurrentUserFromStorage().id;
      const isOwner = map.owner?.id === currentUserId;
      const isSharedWithMe = !isOwner && map.userRoles?.some(r => r.user.id === currentUserId);
      // @ts-ignore
      const isSharedByMe = isOwner && map.sharedWith?.length > 0;
      // MAP-1541: the count predicate had drifted from the list filter and was
      // missing ownedByMe/managedByMe, so those filters counted 0. Keep it in
      // sync with the list filter above.
      const isManager = map.userRoles?.some(r => r.user.id === currentUserId && r.role === 'manager');
      const isManagedByMe = isOwner || isManager;

      const canCreateFromTemplate = isOwner || map.allowViewersToCreateFromTemplate;
      return (
        filterKey === 'all' ||
        (filterKey === 'ownedByMe' && isOwner) ||
        (filterKey === 'managedByMe' && isManagedByMe) ||
        (filterKey === 'sharedByMe' && isSharedByMe) ||
        (filterKey === 'sharedWithMe' && isSharedWithMe) ||
        (filterKey === 'template' && map.isTemplate && canCreateFromTemplate) ||
        (filterKey === 'smaplet' && map.isPublic) ||
        (filterKey === 'regular' && !map.isTemplate && !map.isPublic)
      );
    });

    const finalFiltered = filteredByFilter.filter(map =>
      map.name.toLowerCase().includes(searchText) ||
      map.description?.toLowerCase().includes(searchText)
    );

    if (isSearchActive || filterKey !== 'all') {
      return this.translate.instant('MAP_LIST.COUNT_FILTERED', { count: finalFiltered.length, total: this.maps.length });
    } else {
      return this.translate.instant('MAP_LIST.COUNT_ALL', { total: this.maps.length });
    }
  }

  isInLibrary(map: GraphMap): boolean {
    return this.storeService.isMapInUserLibrary(map.id);
  }

  /**
   * Check if the smap is explicitly shared with the current user (via sharedWith).
   * Sharing takes precedence over library addition - users with shared access
   * should see "Remove" (person_remove) not "Remove from Collection".
   */
  isSharedWithMe(map: GraphMap): boolean {
    const currentUserId = this.userService.getCurrentUserFromStorage()?.id;
    if (!currentUserId || !map.sharedWith) return false;
    return map.sharedWith.some(user => user.id === currentUserId);
  }

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }

  dismiss() {
    this.hideAllTooltips();
    this.bottomSheetRef.dismiss();
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
}
